import React, { createContext, useContext, useState } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface SnackOptions {
	message: string;
	severity?: AlertColor;
	duration?: number;
}

interface SnackbarContextType {
	showSnack: (options: SnackOptions) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
	undefined,
);

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [snack, setSnack] = useState<SnackOptions & { open: boolean }>({
		open: false,
		message: '',
		severity: 'success',
		duration: 4000,
	});

	const showSnack = ({
		message,
		severity = 'success',
		duration = 4000,
	}: SnackOptions) => {
		setSnack({ open: true, message, severity, duration });
	};

	const handleClose = (_?: React.SyntheticEvent | Event, reason?: string) => {
		if (reason === 'clickaway') return;
		setSnack((prev) => ({ ...prev, open: false }));
	};

	return (
		<SnackbarContext.Provider value={{ showSnack }}>
			{children}
			<Snackbar
				open={snack.open}
				autoHideDuration={snack.duration}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={handleClose}
					severity={snack.severity}
					variant="filled"
					sx={{ width: '100%' }}
				>
					{snack.message}
				</Alert>
			</Snackbar>
		</SnackbarContext.Provider>
	);
};

export const useSnack = () => {
	const context = useContext(SnackbarContext);
	if (!context)
		throw new Error('useSnack must be used within a SnackbarProvider');
	return context;
};
